import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Pagination from '@mui/material/Pagination';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Assignment from '@mui/icons-material/Assignment';
import AttachFile from '@mui/icons-material/AttachFile';
import Block from '@mui/icons-material/Block';
import ChatBubbleOutline from '@mui/icons-material/ChatBubbleOutline';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import EditOutlined from '@mui/icons-material/EditOutlined';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import PersonOutline from '@mui/icons-material/PersonOutline';
import PrintOutlined from '@mui/icons-material/PrintOutlined';
import ReportProblemOutlined from '@mui/icons-material/ReportProblemOutlined';
import ScheduleOutlined from '@mui/icons-material/ScheduleOutlined';
import SearchIcon from '@mui/icons-material/Search';
import portalAxios from '../../api/portalAxios';
import { getPortalTicketEndpoint, PORTAL_JOBS_ENDPOINT } from '../../consts';
import { usePortalAuthContext } from '../../contexts/portalAuth';
import { CLIENT_PORTAL_MODULE, PortalJob, PortalJobTask } from '../../types/portal';
import { formatDateTime } from '../../utils';
import { extractPortalError } from './portalError';
import { PortalJobDate } from './portalJobDate.component';
import { PortalSectionHeader } from './portalSectionHeader.component';
import { PortalStatusChip } from './portalStatusChip.component';
import { PortalTicketForm } from './portalTicketForm.component';
import styles from './portal.module.css';

const PAGE_SIZE = 10;
// Only a job that has actually been carried out can be complained about
// (JobStatus.FINISHED in backend/service/consts.py).
const FINISHED_STATUS = 'F';
const SEARCH_DEBOUNCE_MS = 300;

const equipmentLabel = (task: PortalJobTask) => {
  if (!task.equipment) return null;

  return [task.equipment.producer_name, task.equipment.model_name, task.equipment.serial]
    .filter(Boolean)
    .join(' ');
};

export const PortalJobs = () => {
  const { t } = useTranslation();
  const { hasModule } = usePortalAuthContext();

  const [jobs, setJobs] = useState<PortalJob[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [search, setSearch] = useState('');
  // What is actually sent - debounced so typing does not fire a request per
  // keystroke.
  const [appliedSearch, setAppliedSearch] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [editedJob, setEditedJob] = useState<PortalJob | null>(null);
  const [complainedJob, setComplainedJob] = useState<PortalJob | null>(null);
  const [deletedJob, setDeletedJob] = useState<PortalJob | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const canSeeJobs = hasModule(CLIENT_PORTAL_MODULE.TECH_SUPPORT);

  const load = useCallback(async () => {
    try {
      const response = await portalAxios.get(PORTAL_JOBS_ENDPOINT, {
        params: {
          page,
          page_size: PAGE_SIZE,
          ...(appliedSearch ? { search: appliedSearch } : {}),
        },
      });
      setJobs(response.data.results ?? []);
      setPageCount(Math.max(1, Math.ceil((response.data.count ?? 0) / PAGE_SIZE)));
    } catch {
      setFailed(true);
    }
  }, [page, appliedSearch]);

  useEffect(() => {
    if (canSeeJobs) load();
  }, [canSeeJobs, load]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedSearch(search.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [search]);

  const handleDelete = async () => {
    if (!deletedJob) return;

    setDeleting(true);
    setActionError(null);
    try {
      await portalAxios.delete(getPortalTicketEndpoint(deletedJob.id));
      setDeletedJob(null);
      load();
    } catch (error) {
      setActionError(extractPortalError(error, t('We could not withdraw this report.')));
    } finally {
      setDeleting(false);
    }
  };

  if (!canSeeJobs) {
    return (
      <Box className={styles.placeholder}>
        <Block sx={{ fontSize: 40 }} />
        <Typography variant="body2">
          {t('Your service provider has not enabled this section.')}
        </Typography>
      </Box>
    );
  }

  if (failed) {
    return (
      <Alert severity="error">{t('We could not load your jobs. Please try again later.')}</Alert>
    );
  }

  const header = (
    <>
      <PortalSectionHeader
        icon={<Assignment />}
        title={t('Service jobs')}
        description={t('Everything your service provider is working on for you.')}
      />
      <TextField
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder={t('Search by job number or serial number')}
        size="small"
        fullWidth
        sx={{ mt: 2 }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
      />
    </>
  );

  if (jobs === null) {
    return (
      <>
        {header}
        <Box className={styles.centered}>
          <CircularProgress />
        </Box>
      </>
    );
  }

  return (
    <>
      {header}

      {jobs.length === 0 ? (
        <Box className={styles.placeholder}>
          <Assignment sx={{ fontSize: 40 }} />
          <Typography variant="body2">
            {appliedSearch
              ? t('No jobs match your search.')
              : t('There are no service jobs for your company yet.')}
          </Typography>
        </Box>
      ) : (
        <Box className={styles.stack}>
          {jobs.map((job) => {
            const expanded = expandedId === job.id;
            const hasDetails =
              Boolean(job.description) ||
              job.tasks.some((task) => task.attachments.length > 0);

            return (
              <Paper key={job.id} elevation={0} className={styles.cardItem}>
                <Box className={styles.jobRow}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {job.number}
                    </Typography>
                    {job.is_complaint ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        color="warning"
                        label={
                          job.complaint_for_number
                            ? `${t('Complaint')}: ${job.complaint_for_number}`
                            : t('Complaint')
                        }
                      />
                    ) : null}
                    {job.reported_via_portal ? (
                      <Tooltip title={t('Reported by you')}>
                        <PersonOutline fontSize="small" color="action" />
                      </Tooltip>
                    ) : null}
                  </Box>
                  <PortalStatusChip status={job.status} label={job.status_display} />
                </Box>

                <Box className={styles.jobMeta}>
                  <PortalJobDate job={job} />
                  <Box className={styles.metaItem}>
                    <ScheduleOutlined fontSize="small" />
                    {t('Reported at')}: {formatDateTime(job.created_at)}
                  </Box>
                </Box>

                {job.tasks.length > 0 ? (
                  <Box className={styles.attachmentRow}>
                    {job.tasks.map((task) => (
                      <Chip
                        key={task.id}
                        size="small"
                        variant="outlined"
                        icon={<PrintOutlined />}
                        label={equipmentLabel(task) || t('Not specified')}
                      />
                    ))}
                  </Box>
                ) : null}

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    mt: 1.5,
                  }}
                >
                  {hasDetails ? (
                    <Button
                      size="small"
                      startIcon={expanded ? <ExpandLess /> : <ExpandMore />}
                      onClick={() => setExpandedId(expanded ? null : job.id)}
                    >
                      {expanded ? t('Hide details') : t('Show details')}
                    </Button>
                  ) : (
                    <Box />
                  )}

                  {/* Only the actions this job actually allows are rendered. */}
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {job.status === FINISHED_STATUS && !job.is_complaint ? (
                      <Tooltip title={t('Raise a complaint')}>
                        <IconButton
                          size="small"
                          color="warning"
                          aria-label={t('Raise a complaint')}
                          onClick={() => setComplainedJob(job)}
                        >
                          <ReportProblemOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                    {job.can_edit ? (
                      <>
                        <Tooltip title={t('Edit report')}>
                          <IconButton
                            size="small"
                            color="primary"
                            aria-label={t('Edit report')}
                            onClick={() => setEditedJob(job)}
                          >
                            <EditOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('Withdraw report')}>
                          <IconButton
                            size="small"
                            color="error"
                            aria-label={t('Withdraw report')}
                            onClick={() => {
                              setActionError(null);
                              setDeletedJob(job);
                            }}
                          >
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : null}
                  </Box>
                </Box>

                <Collapse in={expanded} unmountOnExit>
                  <Divider sx={{ my: 1.5 }} />
                  {job.description ? (
                    <Box>
                      <Box className={styles.sectionHeader} sx={{ mb: 1 }}>
                        <ChatBubbleOutline fontSize="small" color="primary" />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {t('What you reported')}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {job.description}
                      </Typography>
                    </Box>
                  ) : null}

                  {/* Files hang off the machine they were sent in for, so
                      they are listed under it rather than in one pile. */}
                  {job.tasks
                    .filter((task) => task.attachments.length > 0)
                    .map((task) => (
                      <Box key={task.id} sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          {equipmentLabel(task) || t('Not specified')}
                        </Typography>
                        <Box className={styles.attachmentRow} sx={{ mt: 0.5 }}>
                          {task.attachments.map((attachment) => (
                            <Chip
                              key={attachment.id}
                              size="small"
                              icon={<AttachFile />}
                              label={attachment.attachment.split('/').pop()}
                              component="a"
                              href={attachment.attachment}
                              target="_blank"
                              rel="noopener noreferrer"
                              clickable
                            />
                          ))}
                        </Box>
                      </Box>
                    ))}
                </Collapse>
              </Paper>
            );
          })}
        </Box>
      )}

      {pageCount > 1 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_event, value) => setPage(value)}
            color="primary"
            shape="rounded"
            size="small"
          />
        </Box>
      ) : null}

      <Dialog open={Boolean(editedJob)} onClose={() => setEditedJob(null)} fullWidth maxWidth="sm">
        <DialogTitle>{t('Edit report')}</DialogTitle>
        <DialogContent>
          {editedJob ? (
            <PortalTicketForm
              job={editedJob}
              onDone={() => {
                setEditedJob(null);
                load();
              }}
              onCancel={() => setEditedJob(null)}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(complainedJob)}
        onClose={() => setComplainedJob(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t('Raise a complaint')}</DialogTitle>
        <DialogContent>
          {complainedJob ? (
            <PortalTicketForm
              complaintForJob={complainedJob}
              onDone={() => {
                setComplainedJob(null);
                setPage(1);
                load();
              }}
              onCancel={() => setComplainedJob(null)}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(deletedJob)} onClose={() => setDeletedJob(null)}>
        <DialogTitle>{t('Withdraw report')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('This will remove the report from your service provider. It cannot be undone.')}
          </Typography>
          {actionError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {actionError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setDeletedJob(null)}>
            {t('Cancel')}
          </Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {t('Withdraw report')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
